import { useRef, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  MAX_ATTACHMENTS_COUNT,
  MAX_ATTACHMENTS_TOTAL_BYTES,
  MAX_ATTACHMENT_FILE_BYTES,
  MAX_IMAGE_DIMENSION,
  MAX_IMAGE_FILE_BYTES,
  SNIFF_BYTES,
  TEXT_EXTENSIONS,
  classifyAttachmentBytes,
  formatBytes,
  type Classification,
} from '@runloop/reflex-contract';
import type { Attachment } from '@runloop/reflex-contract';
import { compressImageToLimit } from '../lib/compressImage';

function sumBytes(attachments: Attachment[]): number {
  return attachments.reduce((total, att) => total + (att.size ?? 0), 0);
}

/**
 * `accept` value passed to the file picker. The OS picker treats this as a
 * hint only — the real authority is {@link classifyAttachmentBytes}, which
 * sniffs the file's head bytes after selection. We list well-known MIME
 * groups plus the explicit text/code extensions so common files (PDFs,
 * `.patch`, `Dockerfile`, etc.) show up by default in the picker.
 */
const ACCEPTED_TYPES = ['image/*', 'application/pdf', 'text/*', ...TEXT_EXTENSIONS].join(',');

/** `accept` value for models that reject image input — drops the `image/*` group. */
const ACCEPTED_TYPES_NO_IMAGES = ['application/pdf', 'text/*', ...TEXT_EXTENSIONS].join(',');

export interface UseFileAttachmentsOptions {
  /**
   * Whether the target model accepts image input. When `false`, image files
   * (pasted, dropped, or picked) are rejected with a toast and the file picker
   * hides the image group. Defaults to `true`.
   */
  allowImages?: boolean;
}

let nextLocalId = 0;

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

async function classifyFile(file: File): Promise<Classification> {
  const slice = file.slice(0, SNIFF_BYTES);
  const buf = await slice.arrayBuffer();
  return classifyAttachmentBytes({
    name: file.name,
    mimeType: file.type ?? '',
    head: new Uint8Array(buf),
  });
}

function rejectionMessage(file: File, classification: Classification & { kind: 'rejected' }) {
  switch (classification.reason) {
    case 'binary':
      return `"${file.name}" looks like a binary file (the model can't read it)`;
    case 'invalid-utf8':
      return `"${file.name}" isn't valid UTF-8 text`;
    case 'unknown':
    default:
      return `"${file.name}" is not a supported file type`;
  }
}

export interface UseFileAttachmentsReturn {
  attachments: Attachment[];
  uploading: number;
  hasUploadsInFlight: boolean;
  /** Combined raw size of all attachments, in bytes. */
  totalBytes: number;
  /** Maximum combined raw size allowed, in bytes. */
  maxTotalBytes: number;
  /** True when {@link totalBytes} exceeds {@link maxTotalBytes}. */
  isOverLimit: boolean;
  isDragOver: boolean;
  addFiles: (files: FileList | File[]) => Promise<void>;
  removeAttachment: (id: string) => void;
  clearAttachments: () => void;
  setAttachments: (attachments: Attachment[]) => void;
  pasteHandler: (e: React.ClipboardEvent) => void;
  dragHandlers: {
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  fileInputProps: {
    ref: React.RefObject<HTMLInputElement | null>;
    type: 'file';
    multiple: true;
    accept: string;
    className: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
  openFilePicker: () => void;
}

export function useFileAttachments(
  initialAttachments?: Attachment[],
  options?: UseFileAttachmentsOptions,
): UseFileAttachmentsReturn {
  const allowImages = options?.allowImages ?? true;
  const [attachments, setAttachments] = useState<Attachment[]>(initialAttachments || []);
  const [uploading, setUploading] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const remaining = MAX_ATTACHMENTS_COUNT - attachments.length;
      if (fileArray.length > remaining) {
        toast.error(`Maximum ${MAX_ATTACHMENTS_COUNT} attachments allowed`);
      }
      const toProcess = fileArray.slice(0, Math.max(0, remaining));
      // Track the running total as we add so a batch can't slip past the
      // combined limit (each `setAttachments` is async, so we can't rely on
      // `attachments` updating mid-loop).
      let runningTotal = sumBytes(attachments);
      for (const file of toProcess) {
        // Hard ceiling for every file. Images above this are too big to
        // meaningfully compress, so reject rather than attempt it.
        if (file.size > MAX_ATTACHMENT_FILE_BYTES) {
          toast.error(`"${file.name}" exceeds the ${formatBytes(MAX_ATTACHMENT_FILE_BYTES)} limit`);
          continue;
        }
        setUploading((n) => n + 1);
        try {
          const classification = await classifyFile(file);
          if (classification.kind === 'rejected') {
            toast.error(rejectionMessage(file, classification));
            continue;
          }

          // The selected model is text-only — reject images rather than send
          // them and have the model error ("does not support image input").
          if (classification.kind === 'image' && !allowImages) {
            toast.error(`The selected model can't read images, so "${file.name}" wasn't attached`);
            continue;
          }

          // Images must satisfy Anthropic's per-image byte and dimension caps.
          // Downscale/recompress toward those limits; reject only if it still
          // doesn't fit afterward.
          let finalFile = file;
          let wasCompressed = false;
          if (classification.kind === 'image') {
            const result = await compressImageToLimit(file, {
              maxBytes: MAX_IMAGE_FILE_BYTES,
              maxDimension: MAX_IMAGE_DIMENSION,
            });
            if (!result) {
              toast.error(`"${file.name}" is too large to send even after compression`);
              continue;
            }
            if (result.wasCompressed) {
              toast.warning(
                `Compressed "${file.name}" to fit the ${formatBytes(MAX_IMAGE_FILE_BYTES)} image limit`,
              );
              finalFile = result.file;
              wasCompressed = true;
            }
          }

          if (runningTotal + finalFile.size > MAX_ATTACHMENTS_TOTAL_BYTES) {
            toast.error(
              `Adding "${file.name}" would exceed the ${formatBytes(
                MAX_ATTACHMENTS_TOTAL_BYTES,
              )} total attachment limit`,
            );
            continue;
          }
          runningTotal += finalFile.size;

          const data = await readFileAsBase64(finalFile);
          const att: Attachment = {
            id: `local-${++nextLocalId}`,
            type: classification.kind === 'image' ? 'image' : 'file',
            // When compressed, the bytes/mime/extension changed, so reflect the
            // re-encoded file rather than the original.
            name: wasCompressed ? finalFile.name : file.name,
            mimeType: wasCompressed ? finalFile.type : classification.mimeType,
            size: finalFile.size,
            data,
          };
          setAttachments((prev) => [...prev, att]);
        } catch (err) {
          toast.error(`Failed to read "${file.name}"`, {
            description: err instanceof Error ? err.message : 'Read failed',
          });
        } finally {
          setUploading((n) => n - 1);
        }
      }
    },
    [attachments.length, allowImages],
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  const setAttachmentsCallback = useCallback((newAttachments: Attachment[]) => {
    setAttachments(newAttachments);
  }, []);

  const pasteHandler = useCallback(
    (e: React.ClipboardEvent) => {
      const files = e.clipboardData.files;
      if (files.length > 0) {
        e.preventDefault();
        void addFiles(files);
      }
    },
    [addFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        void addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const fileInputOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        void addFiles(e.target.files);
        e.target.value = '';
      }
    },
    [addFiles],
  );

  const totalBytes = useMemo(() => sumBytes(attachments), [attachments]);

  return {
    attachments,
    uploading,
    hasUploadsInFlight: uploading > 0,
    totalBytes,
    maxTotalBytes: MAX_ATTACHMENTS_TOTAL_BYTES,
    isOverLimit: totalBytes > MAX_ATTACHMENTS_TOTAL_BYTES,
    isDragOver,
    addFiles,
    removeAttachment,
    clearAttachments,
    setAttachments: setAttachmentsCallback,
    pasteHandler,
    dragHandlers: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
    fileInputProps: {
      ref: fileInputRef,
      type: 'file' as const,
      multiple: true as const,
      accept: allowImages ? ACCEPTED_TYPES : ACCEPTED_TYPES_NO_IMAGES,
      className: 'hidden',
      onChange: fileInputOnChange,
    },
    openFilePicker,
  };
}
